/**
 * API client.
 *
 * Holds the access token in memory and the refresh token in localStorage. Keeping
 * the short-lived token out of storage limits what an XSS bug can walk away with;
 * the refresh token has to survive a page reload, so it is stored, rotated on every
 * use, and revoked server-side the moment it is replayed.
 */
/**
 * Where the API lives.
 *
 * Defaults to a same-origin path, which is what you get when nginx proxies
 * /api on the same host the app is served from — no CORS involved, because the
 * browser never sees a second origin.
 *
 * Set VITE_API_BASE_URL at build time to point at a separate API host instead,
 * e.g. https://hr-api.sevenmentor.io/api/v1. That host must then allow this
 * origin through CORS_ORIGIN, or every request fails the preflight.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');
const REFRESH_KEY = 'peopleos.refresh';

let accessToken = null;
let refreshing = null;
const listeners = new Set();

export const onAuthChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const notify = () => listeners.forEach((fn) => fn(accessToken));

export function setTokens({ accessToken: access, refreshToken }) {
  accessToken = access ?? null;
  try {
    if (refreshToken) window.localStorage.setItem(REFRESH_KEY, refreshToken);
    else if (refreshToken === null) window.localStorage.removeItem(REFRESH_KEY);
  } catch { /* private browsing */ }
  notify();
}

export const getAccessToken = () => accessToken;

/**
 * Replaces the access token without touching the stored refresh token.
 *
 * Used by "view as": the administrator keeps their own refresh token, so
 * returning to their account is a refresh and a page reload ends the view on
 * its own.
 */
export function setAccessToken(token) {
  accessToken = token ?? null;
  notify();
}
export const getRefreshToken = () => { try { return window.localStorage.getItem(REFRESH_KEY); } catch { return null; } };

export function clearTokens() {
  accessToken = null;
  try { window.localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  notify();
}

export class ApiError extends Error {
  constructor(status, payload) {
    super(payload?.error?.message ?? `Request failed with ${status}`);
    this.status = status;
    this.code = payload?.error?.code;
    this.details = payload?.error?.details;
    this.requestId = payload?.error?.requestId;
  }
}

export async function refreshSession() {
  const token = getRefreshToken();
  if (!token) throw new ApiError(401, { error: { message: 'Session expired' } });

  // One refresh in flight at a time, otherwise a burst of 401s would each try to
  // rotate the token and all but the first would fail against a burned token.
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: token }),
    })
      .then(async (res) => {
        if (!res.ok) { clearTokens(); throw new ApiError(res.status, await res.json().catch(() => null)); }
        const body = await res.json();
        setTokens(body.data);
        return body.data.accessToken;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export async function api(path, { method = 'GET', body, params, retry = true } = {}) {
  // BASE may be absolute (separate API host) or a path (same-origin proxy);
  // the second argument is ignored when the first is already absolute.
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
    }
  }

  const res = await fetch(url, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && retry && getRefreshToken()) {
    await refreshSession();
    return api(path, { method, body, params, retry: false });
  }

  if (res.status === 204) return null;

  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload;
}

/**
 * Uploads a file as multipart/form-data.
 *
 * Deliberately not routed through api(): fetch sets the multipart boundary on
 * the Content-Type header itself, and it can only do that if we do not set the
 * header. Passing a FormData body through the JSON path would stringify it to
 * "[object Object]".
 */
export async function upload(path, formData, { retry = true } = {}) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    body: formData,
  });

  if (res.status === 401 && retry && getRefreshToken()) {
    await refreshSession();
    return upload(path, formData, { retry: false });
  }

  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload;
}

/**
 * Fetches a file and hands it to the browser as a download.
 *
 * A plain link cannot be used: the endpoint needs an Authorization header, and
 * an <a href> carries no headers. So the bytes are fetched, turned into a blob
 * URL, and a synthetic click starts the save. The object URL is revoked
 * afterwards or the blob stays in memory for the life of the page.
 */
export async function download(path, fallbackName = 'download', { retry = true } = {}) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  const res = await fetch(url, {
    headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
  });

  if (res.status === 401 && retry && getRefreshToken()) {
    await refreshSession();
    return download(path, fallbackName, { retry: false });
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ApiError(res.status, payload);
  }

  // The server names the file in Content-Disposition; fall back to the caller's
  // name when a proxy has stripped the header.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const name = match?.[1] ?? fallbackName;

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return name;
}

export const get = (path, params) => api(path, { params });
export const post = (path, body) => api(path, { method: 'POST', body });
export const patch = (path, body) => api(path, { method: 'PATCH', body });
export const put = (path, body) => api(path, { method: 'PUT', body });
export const del = (path) => api(path, { method: 'DELETE' });
